import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Bell, BellRing, Volume2, VolumeX, Phone, MapPin, Clock, ShoppingBag, User, Check, X, Truck, Store } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { apiRequest, formatPrice } from '../lib/utils';
import { toast } from 'sonner';

const OrderNotification = ({ onOrderAction }) => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isAlarmPlaying, setIsAlarmPlaying] = useState(false);
  const audioContextRef = useRef(null);
  const alarmIntervalRef = useRef(null);
  const vibrationIntervalRef = useRef(null);
  const wakeLockRef = useRef(null);
  const lastCheckedRef = useRef(new Date().toISOString());
  const acknowledgedOrdersRef = useRef(new Set());

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Acquire wake lock to keep screen on
  const acquireWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator && !wakeLockRef.current) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      }
    } catch (e) {
      // Wake lock not supported or denied
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
    }
  }, []);

  // Play loud alarm sound that loops
  const startAlarm = useCallback(() => {
    if (!soundEnabled || isAlarmPlaying) return;
    setIsAlarmPlaying(true);

    const playAlarmCycle = () => {
      try {
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = audioContextRef.current;
        if (ctx.state === 'suspended') ctx.resume();

        // Loud urgent alarm pattern
        const playTone = (startTime, freq, duration) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = freq;
          osc.type = 'square';
          gain.gain.setValueAtTime(0.5, startTime);
          gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
          osc.start(startTime);
          osc.stop(startTime + duration);
        };

        const now = ctx.currentTime;
        // Urgent pattern: high-low-high-low
        playTone(now, 1200, 0.15);
        playTone(now + 0.2, 800, 0.15);
        playTone(now + 0.4, 1200, 0.15);
        playTone(now + 0.6, 800, 0.15);
        playTone(now + 0.8, 1400, 0.3);
      } catch (e) {
        console.error('Alarm error:', e);
      }
    };

    playAlarmCycle();
    alarmIntervalRef.current = setInterval(playAlarmCycle, 2000);

    // Vibrate on mobile
    if ('vibrate' in navigator) {
      navigator.vibrate([500, 200, 500, 200, 500]);
      vibrationIntervalRef.current = setInterval(() => {
        navigator.vibrate([500, 200, 500, 200, 500]);
      }, 2500);
    }
  }, [soundEnabled, isAlarmPlaying]);

  const stopAlarm = useCallback(() => {
    setIsAlarmPlaying(false);
    if (alarmIntervalRef.current) {
      clearInterval(alarmIntervalRef.current);
      alarmIntervalRef.current = null;
    }
    if (vibrationIntervalRef.current) {
      clearInterval(vibrationIntervalRef.current);
      vibrationIntervalRef.current = null;
    }
    if ('vibrate' in navigator) {
      navigator.vibrate(0);
    }
    releaseWakeLock();
  }, [releaseWakeLock]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAlarm();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopAlarm]);

  // Handle accept/decline
  const handleOrderAction = async (orderId, action) => {
    try {
      const newStatus = action === 'accept' ? 'confirmed' : 'cancelled';
      await apiRequest(`/admin/orders/${orderId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ order_status: newStatus })
      });
      toast.success(action === 'accept' ? 'Order accepted!' : 'Order declined');
      acknowledgedOrdersRef.current.add(orderId);
      setPendingOrders(prev => prev.filter(o => o.id !== orderId));
      if (onOrderAction) onOrderAction();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  // When pending orders change, manage alarm
  useEffect(() => {
    if (pendingOrders.length > 0) {
      startAlarm();
      acquireWakeLock();
    } else {
      stopAlarm();
    }
  }, [pendingOrders.length, startAlarm, stopAlarm, acquireWakeLock]);

  // Poll for new orders every 8 seconds
  useEffect(() => {
    const checkNewOrders = async () => {
      try {
        const response = await apiRequest(`/admin/orders/new-count?since=${lastCheckedRef.current}`);
        if (response.count > 0 && response.latest_order) {
          const latestTime = response.latest_order.created_at;
          if (latestTime > lastCheckedRef.current) {
            lastCheckedRef.current = latestTime;

            // Fetch all pending orders for the alert
            const allOrders = await apiRequest('/admin/orders');
            const thirtyMinAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();
            const newPending = allOrders.filter(
              o => !acknowledgedOrdersRef.current.has(o.id) && 
                   o.created_at > thirtyMinAgo &&
                   o.order_status === 'pending'
            );

            if (newPending.length > 0) {
              setPendingOrders(newPending);

              // Browser notification
              if ('Notification' in window && Notification.permission === 'granted') {
                new Notification('NEW ORDER!', {
                  body: `${response.latest_order.user_name} - CHF ${response.latest_order.total?.toFixed(2)}`,
                  icon: '/favicon.ico',
                  tag: 'new-order',
                  requireInteraction: true
                });
              }
            }
          }
        }
      } catch (error) {
        // Silently fail
      }
    };

    checkNewOrders();
    const interval = setInterval(checkNewOrders, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* Sound toggle + Bell in header */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            setSoundEnabled(!soundEnabled);
            if (soundEnabled) stopAlarm();
          }}
          className="relative"
          title={soundEnabled ? 'Mute alarm' : 'Enable alarm'}
          data-testid="sound-toggle"
        >
          {soundEnabled ? (
            <Volume2 className="h-5 w-5 text-green-500" />
          ) : (
            <VolumeX className="h-5 w-5 text-zinc-500" />
          )}
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
        >
          {pendingOrders.length > 0 ? (
            <BellRing className="h-5 w-5 text-[#FFC107] animate-bounce" />
          ) : (
            <Bell className="h-5 w-5" />
          )}
          {pendingOrders.length > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold animate-pulse">
              {pendingOrders.length > 9 ? '9+' : pendingOrders.length}
            </span>
          )}
        </Button>
      </div>

      {/* Fullscreen Order Alert Overlay */}
      {pendingOrders.length > 0 && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/95 flex flex-col items-center overflow-y-auto"
          data-testid="order-alert-overlay"
        >
          {/* Pulsing header */}
          <div className="w-full bg-red-600 py-4 px-4 text-center animate-pulse sticky top-0 z-10">
            <div className="flex items-center justify-center gap-3">
              <BellRing className="h-8 w-8 text-white animate-bounce" />
              <h1 className="text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">
                {pendingOrders.length === 1 ? 'New Order!' : `${pendingOrders.length} New Orders!`}
              </h1>
              <BellRing className="h-8 w-8 text-white animate-bounce" />
            </div>
          </div>

          {/* Order cards */}
          <div className="w-full max-w-lg mx-auto p-4 space-y-6 pb-8">
            {pendingOrders.map((order) => (
              <div 
                key={order.id} 
                className="bg-[#141417] border-2 border-[#FFC107] rounded-2xl overflow-hidden shadow-2xl shadow-[#FFC107]/20"
                data-testid={`order-alert-${order.id}`}
              >
                {/* Order header */}
                <div className="bg-[#FFC107] px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-black font-bold text-lg">#{order.id?.slice(0, 8)}</span>
                    <Badge className="bg-black text-[#FFC107] text-sm px-3 py-1">
                      {order.order_type === 'delivery' ? (
                        <><Truck className="h-4 w-4 mr-1 inline" /> Delivery</>
                      ) : (
                        <><Store className="h-4 w-4 mr-1 inline" /> Pickup</>
                      )}
                    </Badge>
                  </div>
                </div>

                {/* Customer info */}
                <div className="px-4 py-3 space-y-2 border-b border-[#27272A]">
                  <div className="flex items-center gap-2 text-white">
                    <User className="h-4 w-4 text-[#FFC107] shrink-0" />
                    <span className="font-semibold text-lg">{order.user_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-300">
                    <Phone className="h-4 w-4 text-[#FFC107] shrink-0" />
                    <a href={`tel:${order.user_phone}`} className="underline text-blue-400">{order.user_phone}</a>
                  </div>
                  {order.delivery_address && (
                    <div className="flex items-start gap-2 text-zinc-300">
                      <MapPin className="h-4 w-4 text-[#FFC107] shrink-0 mt-0.5" />
                      <span className="text-sm">{order.delivery_address}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Clock className="h-4 w-4 text-[#FFC107] shrink-0" />
                    <span className="text-sm">{new Date(order.created_at).toLocaleTimeString()}</span>
                  </div>
                  {order.scheduled_date && order.scheduled_time && (
                    <div className="flex items-center gap-2 mt-1 p-2 rounded bg-[#FFC107]/10">
                      <Clock className="h-4 w-4 text-[#FFC107] shrink-0" />
                      <span className="text-sm font-bold text-[#FFC107]">
                        Scheduled: {order.scheduled_date} at {order.scheduled_time}
                      </span>
                    </div>
                  )}
                </div>

                {/* Order items */}
                <div className="px-4 py-3 border-b border-[#27272A]">
                  <div className="flex items-center gap-2 mb-2">
                    <ShoppingBag className="h-4 w-4 text-[#FFC107]" />
                    <span className="font-semibold text-white">Items</span>
                  </div>
                  {order.items?.map((item, i) => (
                    <div key={i} className="py-1.5 border-b border-yellow-500/10 last:border-0">
                      <div className="flex justify-between text-white">
                        <span>{item.quantity}x {item.name}</span>
                        <span className="text-[#FFC107] font-medium">{formatPrice(item.price * item.quantity)}</span>
                      </div>
                      {item.selected_size && (
                        <span className="text-zinc-400 text-xs ml-6">Size: {item.selected_size === 'big' ? 'Big' : 'Normal'}</span>
                      )}
                      {item.has_combo && (
                        <div className="text-green-400 text-xs ml-6">+ Combo (Drink + Fries)</div>
                      )}
                      {item.selected_addons?.length > 0 && (
                        <div className="text-[#FFC107]/70 text-xs ml-6">+ {item.selected_addons.map(a => a.name).join(', ')}</div>
                      )}
                      {item.removed_ingredients?.length > 0 && (
                        <div className="text-red-400 text-xs ml-6">Without: {item.removed_ingredients.join(', ')}</div>
                      )}
                      {item.special_instructions && (
                        <div className="text-zinc-400 text-xs ml-6">Note: {item.special_instructions}</div>
                      )}
                    </div>
                  ))}
                  {order.notes && (
                    <div className="mt-2 p-2 bg-[#1C1C22] rounded text-sm text-zinc-300">
                      <span className="text-[#FFC107] font-medium">Notes: </span>{order.notes}
                    </div>
                  )}
                </div>

                {/* Total */}
                <div className="px-4 py-3 border-b border-[#27272A]">
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400">Subtotal</span>
                    <span className="text-white">{formatPrice(order.subtotal || order.total)}</span>
                  </div>
                  {order.delivery_fee > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400">Delivery Fee</span>
                      <span className="text-white">{formatPrice(order.delivery_fee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-zinc-700">
                    <span className="text-white font-bold text-xl">TOTAL</span>
                    <span className="text-[#FFC107] font-bold text-2xl">{formatPrice(order.total)}</span>
                  </div>
                  <div className="mt-1">
                    <Badge variant="outline" className="border-zinc-600 text-zinc-300">
                      {order.payment_method === 'cod' ? 'Cash on Delivery' : order.payment_method}
                    </Badge>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="p-4 grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleOrderAction(order.id, 'decline')}
                    className="bg-red-600 hover:bg-red-700 text-white font-bold py-6 text-lg rounded-xl"
                    data-testid={`decline-order-${order.id}`}
                  >
                    <X className="h-6 w-6 mr-2" />
                    Decline
                  </Button>
                  <Button
                    onClick={() => handleOrderAction(order.id, 'accept')}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-6 text-lg rounded-xl"
                    data-testid={`accept-order-${order.id}`}
                  >
                    <Check className="h-6 w-6 mr-2" />
                    Accept
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default OrderNotification;
